import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Plus, Trash2, Dumbbell, Save, X, Calendar as CalendarIcon, Clock, Weight, 
  Flame, TrendingUp, Target, Zap, ChevronDown, ChevronUp, Sparkles, ArrowRight,
  Trophy, Repeat, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import Paywall from "@/components/Paywall";

// Feature card for empty state
function FeatureCard({ icon: Icon, title, description, color }) {
  return (
    <div className={`p-5 rounded-xl bg-gradient-to-br ${color} border border-white/5`}>
      <Icon className="h-8 w-8 mb-3 text-white/80" />
      <h3 className="font-bold text-white mb-1">{title}</h3>
      <p className="text-xs text-white/60">{description}</p>
    </div>
  );
}

export default function TrainingTracker() {
  const { authHeaders } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [workoutName, setWorkoutName] = useState("");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [exercises, setExercises] = useState([{ exercise_name: "", sets: [{ reps: 0, weight: 0 }] }]);
  const [saving, setSaving] = useState(false);
  const [expandedWorkout, setExpandedWorkout] = useState(null);

  const fetchWorkouts = () => {
    axios.get(`${API}/workouts`, { headers: authHeaders() })
      .then(res => setWorkouts(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchWorkouts(); }, []);

  const addExercise = () => setExercises([...exercises, { exercise_name: "", sets: [{ reps: 0, weight: 0 }] }]);

  const addSet = (idx) => {
    const copy = [...exercises];
    copy[idx].sets.push({ reps: 0, weight: 0 });
    setExercises(copy);
  };

  const removeExercise = (idx) => {
    if (exercises.length <= 1) return;
    setExercises(exercises.filter((_, i) => i !== idx));
  };

  const updateExerciseName = (idx, name) => {
    const copy = [...exercises];
    copy[idx].exercise_name = name;
    setExercises(copy);
  };

  const updateSet = (eIdx, sIdx, field, value) => {
    const copy = [...exercises];
    copy[eIdx].sets[sIdx][field] = parseFloat(value) || 0;
    setExercises(copy);
  };

  const removeSet = (eIdx, sIdx) => {
    const copy = [...exercises];
    if (copy[eIdx].sets.length <= 1) return;
    copy[eIdx].sets = copy[eIdx].sets.filter((_, i) => i !== sIdx);
    setExercises(copy);
  };

  const handleSave = async () => {
    if (!workoutName.trim()) { toast.error("Angiv et navn til træningen"); return; }
    if (exercises.some(e => !e.exercise_name.trim())) { toast.error("Alle øvelser skal have et navn"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/workouts`, {
        name: workoutName, exercises,
        duration_minutes: duration ? parseInt(duration) : null,
        notes: notes || null,
      }, { headers: authHeaders() });
      toast.success("Træning gemt!");
      setShowForm(false);
      setWorkoutName(""); setDuration(""); setNotes("");
      setExercises([{ exercise_name: "", sets: [{ reps: 0, weight: 0 }] }]);
      fetchWorkouts();
    } catch {
      toast.error("Fejl ved gemning af træning");
    } finally { setSaving(false); }
  };

  const deleteWorkout = async (id) => {
    try {
      await axios.delete(`${API}/workouts/${id}`, { headers: authHeaders() });
      toast.success("Træning slettet");
      fetchWorkouts();
    } catch { toast.error("Fejl ved sletning"); }
  };

  const totalStats = {
    count: workouts.length,
    totalWeight: workouts.reduce((sum, w) => sum + (w.total_weight || 0), 0),
    totalSets: workouts.reduce((sum, w) => sum + (w.total_sets || 0), 0),
    totalReps: workouts.reduce((sum, w) => sum + (w.total_reps || 0), 0),
  };

  // Get this week's workouts
  const thisWeekWorkouts = workouts.filter(w => {
    const workoutDate = new Date(w.date);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return workoutDate >= weekAgo;
  }).length;

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]" data-testid="training-page">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#DFFF00]/10 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#DFFF00]/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#DFFF00]/20 to-[#DFFF00]/5 border border-[#DFFF00]/20 flex items-center justify-center">
                  <Dumbbell className="h-7 w-7 text-[#DFFF00]" />
                </div>
                <div>
                  <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white">
                    Trænings<span className="text-[#DFFF00]">tracker</span>
                  </h1>
                  <p className="text-sm text-zinc-400 mt-0.5">
                    {workouts.length > 0 ? `${workouts.length} træninger logget` : "Log og følg dine træninger"}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Link to="/ai-coach">
                  <Button variant="outline" className="border-[#DFFF00]/30 text-[#DFFF00] hover:bg-[#DFFF00]/10 rounded-xl font-bold uppercase tracking-wider h-12 px-5">
                    <Sparkles className="mr-2 h-4 w-4" /> AI Program
                  </Button>
                </Link>
                <Button onClick={() => setShowForm(!showForm)} data-testid="new-workout-btn"
                  className="bg-gradient-to-r from-[#DFFF00] to-[#C8E600] text-black hover:opacity-90 shadow-lg shadow-[#DFFF00]/20 rounded-xl font-bold uppercase tracking-wider h-12 px-6">
                  <Plus className="mr-2 h-5 w-5" /> Log Træning
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Træninger", value: totalStats.count, icon: Dumbbell, color: "from-[#DFFF00]/20 to-transparent", text: "text-[#DFFF00]", subtext: `${thisWeekWorkouts} denne uge` },
              { label: "Total Vægt", value: `${(totalStats.totalWeight / 1000).toFixed(1)}t`, icon: Weight, color: "from-blue-500/20 to-transparent", text: "text-blue-400", subtext: `${Math.round(totalStats.totalWeight).toLocaleString()} kg` },
              { label: "Total Sæt", value: totalStats.totalSets.toLocaleString(), icon: Repeat, color: "from-orange-500/20 to-transparent", text: "text-orange-400", subtext: `${totalStats.totalReps.toLocaleString()} reps` },
              { label: "Gns. pr. Træning", value: workouts.length > 0 ? `${Math.round(totalStats.totalWeight / workouts.length)} kg` : "0 kg", icon: BarChart3, color: "from-purple-500/20 to-transparent", text: "text-purple-400", subtext: "per session" },
            ].map(({ label, value, icon: Icon, color, text, subtext }) => (
              <div key={label} className={`bg-gradient-to-br ${color} bg-[#18181b] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-all`}>
                <Icon className={`h-5 w-5 mb-3 ${text}`} />
                <p style={{ fontFamily: 'JetBrains Mono, monospace' }} className={`text-2xl font-bold ${text}`}>{value}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{label}</p>
                {subtext && <p className="text-[10px] text-zinc-600 mt-0.5">{subtext}</p>}
              </div>
            ))}
          </div>

          {/* New Workout Form */}
          {showForm && (
            <div className="bg-gradient-to-br from-[#18181b] to-[#141417] border border-[#DFFF00]/20 rounded-2xl mb-8 overflow-hidden shadow-xl shadow-[#DFFF00]/5" data-testid="workout-form">
              <div className="p-6 border-b border-white/5 bg-gradient-to-r from-[#DFFF00]/10 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center">
                    <Plus className="h-5 w-5 text-[#DFFF00]" />
                  </div>
                  <div>
                    <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-xl tracking-wider text-white">Ny Træning</h2>
                    <p className="text-xs text-zinc-500">Udfyld dine øvelser og sæt</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                      <Target className="h-3 w-3" /> Træningsnavn
                    </Label>
                    <Input value={workoutName} onChange={e => setWorkoutName(e.target.value)} placeholder="F.eks. Brystdag"
                      className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12" data-testid="workout-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                      <Clock className="h-3 w-3" /> Varighed (min)
                    </Label>
                    <Input type="number" value={duration} onChange={e => setDuration(e.target.value)} placeholder="60"
                      className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12" data-testid="workout-duration-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Noter (valgfri)</Label>
                    <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Hvordan gik træningen?"
                      className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12" data-testid="workout-notes-input" />
                  </div>
                </div>

                {/* Exercises */}
                <div className="space-y-4">
                  {exercises.map((ex, eIdx) => (
                    <div key={eIdx} className="bg-zinc-900/30 border border-white/5 rounded-xl p-5 hover:border-[#DFFF00]/20 transition-colors">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-8 h-8 rounded-lg bg-[#DFFF00]/10 flex items-center justify-center">
                          <span style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-sm font-bold text-[#DFFF00]">{eIdx + 1}</span>
                        </span>
                        <Input value={ex.exercise_name} onChange={e => updateExerciseName(eIdx, e.target.value)} placeholder="Øvelsesnavn (f.eks. Bænkpres)"
                          className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-11 flex-1 font-medium" data-testid={`exercise-name-${eIdx}`} />
                        <Button variant="ghost" size="icon" onClick={() => removeExercise(eIdx)} 
                          className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-11 w-11 rounded-lg" data-testid={`remove-exercise-${eIdx}`}>
                          <X className="h-5 w-5" />
                        </Button>
                      </div>
                      
                      {/* Sets */}
                      <div className="space-y-2 ml-11">
                        {ex.sets.map((set, sIdx) => (
                          <div key={sIdx} className="flex items-center gap-3 bg-black/20 rounded-xl p-3">
                            <span style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-xs text-zinc-600 w-12">Sæt {sIdx + 1}</span>
                            <div className="flex items-center gap-2 flex-1">
                              <Input type="number" value={set.reps || ""} onChange={e => updateSet(eIdx, sIdx, "reps", e.target.value)} placeholder="Reps"
                                className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-lg h-10 w-20 text-center" data-testid={`set-reps-${eIdx}-${sIdx}`} />
                              <span className="text-zinc-600 font-bold">×</span>
                              <Input type="number" value={set.weight || ""} onChange={e => updateSet(eIdx, sIdx, "weight", e.target.value)} placeholder="Kg"
                                className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-lg h-10 w-20 text-center" data-testid={`set-weight-${eIdx}-${sIdx}`} />
                              <span className="text-xs text-zinc-500">kg</span>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => removeSet(eIdx, sIdx)} 
                              className="text-zinc-600 hover:text-red-400 h-8 w-8 rounded-lg" data-testid={`remove-set-${eIdx}-${sIdx}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" onClick={() => addSet(eIdx)} 
                          className="text-zinc-500 hover:text-[#DFFF00] text-xs rounded-lg" data-testid={`add-set-${eIdx}`}>
                          <Plus className="h-3 w-3 mr-1" /> Tilføj sæt
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-white/5">
                  <Button variant="outline" onClick={addExercise} data-testid="add-exercise-btn"
                    className="border-[#DFFF00]/20 text-[#DFFF00] hover:bg-[#DFFF00]/10 rounded-xl">
                    <Plus className="mr-2 h-4 w-4" /> Tilføj øvelse
                  </Button>
                  <div className="flex-1" />
                  <Button variant="outline" onClick={() => setShowForm(false)} className="border-white/10 text-zinc-400 rounded-xl">
                    Annuller
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-workout-btn"
                    className="bg-gradient-to-r from-[#DFFF00] to-[#C8E600] text-black hover:opacity-90 rounded-xl font-bold uppercase tracking-wider px-8">
                    <Save className="mr-2 h-4 w-4" /> {saving ? "Gemmer..." : "Gem Træning"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Workout History */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-sm tracking-wider text-zinc-500 flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" /> Træningshistorik
              </h2>
              {workouts.length > 0 && (
                <Link to="/exercises" className="text-xs text-[#DFFF00] hover:underline flex items-center gap-1">
                  Se øvelser <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1,2,3].map(i => <div key={i} className="bg-[#18181b] border border-white/5 rounded-2xl h-28 animate-pulse" />)}
              </div>
            ) : workouts.length === 0 ? (
              <div className="text-center py-12">
                {/* Empty state with features */}
                <div className="max-w-2xl mx-auto">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#DFFF00]/20 to-[#DFFF00]/5 border border-[#DFFF00]/20 flex items-center justify-center">
                    <Dumbbell className="h-10 w-10 text-[#DFFF00]/50" />
                  </div>
                  <h2 style={{ fontFamily: 'Oswald' }} className="text-2xl font-bold uppercase text-white mb-2">
                    Ingen træninger endnu
                  </h2>
                  <p className="text-zinc-400 mb-8 max-w-md mx-auto">
                    Start med at logge din første træning og følg din fremgang over tid!
                  </p>
                  
                  {/* Feature highlights */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <FeatureCard
                      icon={TrendingUp}
                      title="Spor Fremgang"
                      description="Se din udvikling med detaljerede grafer"
                      color="from-[#DFFF00]/20 to-[#DFFF00]/5"
                    />
                    <FeatureCard
                      icon={Trophy}
                      title="Optjen XP"
                      description="Få XP og achievements for hver træning"
                      color="from-orange-500/20 to-orange-500/5"
                    />
                    <FeatureCard
                      icon={Flame}
                      title="Hold Streaken"
                      description="Byg din træningsstreak dag for dag"
                      color="from-red-500/20 to-red-500/5"
                    />
                  </div>
                  
                  <Button onClick={() => setShowForm(true)} 
                    className="bg-gradient-to-r from-[#DFFF00] to-[#C8E600] text-black hover:opacity-90 shadow-lg shadow-[#DFFF00]/20 rounded-xl font-bold uppercase tracking-wider h-12 px-8">
                    <Plus className="mr-2 h-4 w-4" /> Log din første træning
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {workouts.map((w, idx) => (
                  <Card key={w.id} className="bg-[#18181b] border-white/5 rounded-2xl overflow-hidden hover:border-[#DFFF00]/20 transition-all group" data-testid={`workout-${w.id}`}>
                    <div className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Day indicator */}
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#DFFF00]/10 to-transparent flex flex-col items-center justify-center flex-shrink-0">
                            <span style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-[#DFFF00]">
                              {new Date(w.date).getDate()}
                            </span>
                            <span className="text-[9px] uppercase text-zinc-500">
                              {new Date(w.date).toLocaleDateString("da-DK", { month: 'short' })}
                            </span>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                              <h3 style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-lg text-white font-bold">{w.name}</h3>
                              <span style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-sm text-[#DFFF00] bg-[#DFFF00]/10 px-3 py-1 rounded-lg font-bold">
                                {Math.round(w.total_weight || 0).toLocaleString()} kg
                              </span>
                            </div>
                            
                            {/* Quick stats */}
                            <div className="flex items-center gap-4 flex-wrap text-xs text-zinc-500">
                              <span className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-lg">
                                <Dumbbell className="h-3 w-3 text-zinc-400" />
                                {w.exercises?.length} øvelser
                              </span>
                              <span className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-lg">
                                <Repeat className="h-3 w-3 text-zinc-400" />
                                {w.total_sets} sæt
                              </span>
                              {w.duration_minutes && (
                                <span className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-lg">
                                  <Clock className="h-3 w-3 text-zinc-400" />
                                  {w.duration_minutes} min
                                </span>
                              )}
                            </div>
                            
                            {/* Exercise tags (collapsed) */}
                            {expandedWorkout !== w.id && w.exercises && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {w.exercises.slice(0, 4).map((ex, i) => (
                                  <span key={i} className="text-xs bg-zinc-900/80 text-zinc-400 px-3 py-1.5 rounded-lg border border-white/5">
                                    {ex.exercise_name}
                                  </span>
                                ))}
                                {w.exercises.length > 4 && (
                                  <span className="text-xs text-zinc-600 px-2 py-1.5">+{w.exercises.length - 4} flere</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setExpandedWorkout(expandedWorkout === w.id ? null : w.id)}
                            className="text-zinc-500 hover:text-white hover:bg-white/5 rounded-lg">
                            {expandedWorkout === w.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteWorkout(w.id)} 
                            className="text-zinc-600 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" data-testid={`delete-workout-${w.id}`}>
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Expanded exercise details */}
                      {expandedWorkout === w.id && w.exercises && (
                        <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                          {w.exercises.map((ex, i) => (
                            <div key={i} className="bg-zinc-900/30 rounded-xl p-4 border border-white/5">
                              <div className="flex items-center gap-3 mb-3">
                                <span className="w-7 h-7 rounded-lg bg-[#DFFF00]/10 flex items-center justify-center">
                                  <span style={{ fontFamily: 'JetBrains Mono' }} className="text-xs font-bold text-[#DFFF00]">{i+1}</span>
                                </span>
                                <span className="font-bold text-white">{ex.exercise_name}</span>
                              </div>
                              <div className="flex gap-2 flex-wrap ml-10">
                                {ex.sets?.map((s, j) => (
                                  <span key={j} style={{ fontFamily: 'JetBrains Mono' }} 
                                    className="text-xs bg-black/30 border border-white/5 px-3 py-1.5 rounded-lg text-zinc-300">
                                    {s.reps} × {s.weight}kg
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                          {w.notes && (
                            <div className="flex items-start gap-3 p-4 bg-[#DFFF00]/5 rounded-xl border border-[#DFFF00]/10">
                              <Zap className="h-4 w-4 text-[#DFFF00] flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-[#DFFF00]">{w.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Paywall>
  );
}
